import { useState, useRef, useEffect } from "react";

interface DropdownOption {
  value: number;
  label: string;
}

type Props = {
  mallSelected: (value: number) => void;
  mallOptions: DropdownOption[];
  tenantSelected: (value: number) => void;
  tenantOptions: DropdownOption[];
  mallValue?: number | null;
  tenantValue?: number | null;
  brandSelected?: (value: number) => void;
  brandOptions?: DropdownOption[];
  brandValue?: number | null;
};

type SearchableSelectProps = {
  id: string;
  label: string;
  options: DropdownOption[];
  value: number | null | undefined;
  onChange: (value: number) => void;
  placeholder: string;
  disabled: boolean;
};

function SearchableSelect({
  id,
  label,
  options,
  value,
  onChange,
  placeholder,
  disabled,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);
  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  const handleSelect = (optionValue: number) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm("");
      }
    }
  };

  return (
    <div className="relative w-full sm:w-48" ref={dropdownRef}>
      <label
        htmlFor={id}
        className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5"
      >
        {label}
      </label>
      <button
        type="button"
        className={`w-full px-3 py-2 text-xs font-medium rounded-md border shadow-sm transition-all text-left ${
          options.length === 1
            ? "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed dark:bg-gray-800 dark:text-gray-500 dark:border-gray-700"
            : "bg-white text-gray-700 border-gray-200 hover:border-blue-400 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:border-blue-500 cursor-pointer"
        } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
        onClick={handleToggle}
        disabled={disabled}
      >
        <div className="flex items-center justify-between">
          <span
            className={`truncate ${!selectedOption ? "text-gray-400" : ""}`}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <svg
            className={`w-4 h-4 transition-transform flex-shrink-0 ml-2 ${
              isOpen ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {isOpen && !disabled && (
        <div className="absolute z-50 mt-1 w-full sm:w-48 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg overflow-hidden">
          
          <div className="p-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600">
            <div className="relative">
              <svg
                className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                className="w-full pl-8 pr-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoComplete="off"
              />
            </div>
          </div>

          
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={`px-3 py-2 text-xs cursor-pointer transition-colors ${
                    option.value === value
                      ? "bg-blue-100 dark:bg-gray-600 text-blue-700 dark:text-blue-300 font-medium"
                      : "text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-gray-600"
                  }`}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                </div>
              ))
            ) : (
              <div className="px-3 py-6 text-xs text-gray-400 dark:text-gray-500 text-center">
                <svg
                  className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                No results found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Index({
  mallSelected,
  mallOptions,
  tenantSelected,
  tenantOptions,
  mallValue,
  tenantValue,
}: Props) {
  return (
    <div className="flex items-end gap-3 my-4 w-full sm:w-auto sm:justify-end">
      
      <SearchableSelect
        id="mall"
        label="Location"
        options={mallOptions}
        value={mallValue}
        onChange={mallSelected}
        placeholder="Select Mall"
        disabled={mallOptions.length === 1}
      />

      
      <SearchableSelect
        id="tenant"
        label="Tenant"
        options={tenantOptions}
        value={tenantValue}
        onChange={tenantSelected}
        placeholder="Select Tenant"
        disabled={tenantOptions.length === 1}
      />
    </div>
  );
}
